import { LitElement, css, html, nothing, unsafeCSS, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';

import { tokens } from '../../../theme/tokens.css';
import { type GameMode, uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/** Widest viewport that gets the compact layout: ping, plus the CTF match
 *  timer, stacked in the top-right corner. Feeds both the stylesheet's media
 *  queries and render(), so the markup and its layout switch together. */
const COMPACT_MAX_WIDTH = 959;

/**
 * Game Stats — bots / players / ping / CTF-timer chip.
 *
 * Desktop (>=960px) renders the full row centered at top with all icons
 * positioned above their values. Narrow viewports (<960px) keep the
 * always-useful fields — ping, plus the CTF match timer when active —
 * stacked vertically in the top-right corner below the menu-toolbar.
 * Compact mode shifts to inline-icon-beside-value layout since the desktop
 * icon-above pattern doesn't tile vertically. Bots and player counts are
 * dropped on narrow widths (recoverable from the scoreboard).
 *
 * The 960 threshold is sized for the CTF case (bots + players +
 * team-breakdown + ping + match timer ≈ 340px wide), which needs ~928px to
 * centre-clear of the top-bar's full logo + selector cluster. FFA fits at
 * much lower widths but uses the same threshold for consistency.
 *
 * @element game-stats
 */
@customElement('game-stats')
export class GameStats extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        position: absolute;
        /* Centered via translateX, sized to content (max-content). Replaces
           the old fixed 440px width + negative-margin centering which would
           overflow when crowded-CTF player counts pushed content past 440. */
        left: 50%;
        /* Each top here adds the status-bar inset, in step with the top bar
           (see airmash-game); it's 0 outside an installed app held upright. */
        top: calc(18px + env(safe-area-inset-top, 0px));
        width: max-content;
        transform: translateX(-50%);
        /* The transform above creates a stacking context, trapping the
           tooltips' z-index inside it — and with the host at z auto, the
           later-in-DOM game-mode-status (CTF flags at top: 50px, exactly
           where an open tooltip lands) painted over them. Lift the whole
           context above the passive HUD; nothing overlaps the chips
           themselves, so only tooltip visibility changes. */
        z-index: 20;
        white-space: nowrap;
        text-align: center;
        /* Declared here rather than inherited from the body. The desktop
           tooltips can be hovered, so no text selection either. */
        font-family: var(--font-family);
        font-size: var(--font-size-xs);
        -webkit-user-select: none;
        user-select: none;
        text-shadow: var(--shadow-text);
        color: var(--color-text-tooltip);
        cursor: default;
        pointer-events: none;
      }

      .item {
        display: inline-block;
        color: var(--color-text-white);
        font-weight: bold;
      }

      .icon-container {
        display: inline-block;
        position: relative;
        width: 24px;
      }

      .icon-container.padded {
        margin-left: 10px;
      }

      .icon {
        position: absolute;
        top: -15px;
        left: 0;
        width: 21px;
        height: 21px;
        background-repeat: no-repeat;
        background-size: 11px 11px;
        background-position: 5px 5px;
        background-color: var(--color-bg-chip);
        border-radius: var(--radius-xs);
      }

      /* Tooltip styles */
      .tooltip {
        display: none;
        position: absolute;
        top: 12px;
        left: 50%;
        transform: translateX(-50%);
        min-width: 100px;
        padding: 8px 10px;
        background-color: var(--color-bg-tooltip);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-sm);
        font-weight: normal;
        line-height: 1.4;
        color: var(--color-text-tooltip);
        white-space: normal;
        text-align: center;
        z-index: 20;
      }

      .tooltip .arrow {
        position: absolute;
        top: -6px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 6px solid var(--color-bg-tooltip);
      }

      /* Pointer capture + tooltip are hover-only. The icon chips opt back into
         pointer events solely so the host's pointer-events:none doesn't suppress
         :hover for the tooltip. On touch there is no tooltip, so leaving them
         click-through avoids dead tap-traps over the HUD. Gating under
         (hover: hover) also stops iOS sticky-hover trapping the tooltip in the
         "shown" state after a tap. */
      @media (hover: hover) {
        .icon-container {
          pointer-events: auto;
          cursor: help;
        }

        .icon-container:hover .tooltip {
          display: block;
        }
      }

      .icon.players {
        background-image: url('/vanilla/ui/stats/players.svg');
      }

      .icon.bots {
        background-image: url('/vanilla/ui/stats/bot.svg');
      }

      .icon.ping {
        background-image: url('/vanilla/ui/stats/ping.svg');
      }

      .icon.timer {
        background-image: url('/vanilla/ui/stats/timer.svg');
        /* Lucide stroke icons read smaller than the fill-based ones at 11px;
           bump size and re-center so visual weight matches. */
        background-size: 14px 14px;
        background-position: 3.5px 3.5px;
      }

      .millis {
        margin-left: 3px;
        color: var(--color-text-muted);
      }

      .match-time {
        font-variant-numeric: tabular-nums;
      }

      .greyed {
        color: var(--color-text-muted);
      }

      .blue-team {
        color: var(--color-team-blue);
      }

      .red-team {
        color: var(--color-team-red);
      }

      .active-players {
        color: var(--color-players-active);
      }

      /* Narrow viewports — even the auto-sized chip can overlap top-bar
         items at this width range. Threshold is sized for the CTF case
         (~340px wide with team-breakdown + match timer); FFA uses the
         same threshold for consistency. Tucked into the top-right corner
         below menu-toolbar instead, with each surviving chip (ping, plus
         the CTF match timer when active) stacked vertically.
         These blocks must stay below the base rules they override: at the
         same specificity the later rule wins. Placed first, the icon rules
         never applied, and the stacked rows' icons overlapped. */
      @media (max-width: ${unsafeCSS(COMPACT_MAX_WIDTH)}px) {
        :host {
          left: auto;
          /* Clear of the landscape notch on the right edge; 0 off-notch. */
          right: calc(12px + env(safe-area-inset-right, 0px));
          top: calc(50px + env(safe-area-inset-top, 0px));
          transform: none;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
        }

        /* Inline icon next to value — the desktop "icon above text" pattern
           (icon position:absolute top:-15) overlaps the previous row when
           chips are stacked. */
        .icon-container {
          width: auto;
        }

        .icon-container.padded {
          margin-left: 0;
        }

        .icon {
          position: static;
          display: inline-block;
          vertical-align: middle;
          margin-right: 4px;
        }
      }

      /* Touch toolbar chips are 44px tall (vs 31px), so the menu-toolbar's
         bottom drops to 58px. Push the stacked ping / match-timer column
         clear of it. */
      @media (max-width: ${unsafeCSS(COMPACT_MAX_WIDTH)}px) and (hover: none) {
        :host {
          top: calc(64px + env(safe-area-inset-top, 0px));
        }
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  override render() {
    const { gameStats, gameType, viewport } = this.uiState;

    // Don't render until the game starts and the first stats snapshot lands.
    // Note: we deliberately don't bail on `players === 0` — an empty server
    // or a freshly-cleared scoreboard is still a valid game state, and we
    // want the ping chip to remain visible.
    if (!gameType || !gameStats) {
      return nothing;
    }

    // Compact form below 960px: ping always, CTF match timer below it when
    // active. Both are first-class info — ping tells the user if the
    // connection is playable, the CTF timer is hard to recover elsewhere on
    // mobile. The other fields (bots, player counts) are in the scoreboard.
    if (viewport.width <= COMPACT_MAX_WIDTH) {
      const isCtfTimerActive = gameType === 'ctf' && this.uiState.ctfMatchTimer !== null;
      return html`
        <div class="item">${this.renderPing()}</div>
        ${isCtfTimerActive ? html`<div class="item">${this.renderMatchTimer()}</div>` : nothing}
      `;
    }

    return html`
      <div class="item">
        ${this.renderBots()} ${this.renderPlayers(gameType)} ${this.renderPing()} ${this.renderMatchTimer()}
      </div>
    `;
  }

  /** Render CTF match elapsed time. Defensive: enforces the CTF gate locally
   *  rather than trusting that `ctfMatchTimer` is cleared on game-type
   *  transitions — if a stale value lingers, we still won't surface a timer
   *  in FFA/BTR. */
  private renderMatchTimer() {
    const { gameType, ctfMatchTimer } = this.uiState;
    if (gameType !== 'ctf' || ctfMatchTimer === null) return nothing;
    return html`
      <span class="icon-container padded">
        <span class="icon timer"></span>
        <span class="tooltip">
          <span class="arrow"></span>
          Match elapsed time
        </span>
      </span>
      <span class="match-time">${ctfMatchTimer}</span>
    `;
  }

  /** Render bot count (only if > 0) */
  private renderBots() {
    const { bots } = this.uiState.gameStats;
    if (!bots || bots <= 0) {
      return nothing;
    }

    return html`
      <span class="icon-container">
        <span class="icon bots"></span>
        <span class="tooltip">
          <span class="arrow"></span>
          Bots in game
        </span>
      </span>
      <span class="greyed">${bots}</span>
    `;
  }

  /** Render player count with team breakdown based on game type. Each field
   *  is clamped against missing / negative values from the server before
   *  the activePlayers subtraction — JSON can't carry NaN so we don't guard
   *  against that, but a partial snapshot or a stale value can be negative
   *  or undefined. */
  private renderPlayers(gameType: GameMode) {
    const { players, blueTeam, redTeam, spectators } = this.uiState.gameStats;

    const playersSafe = Math.max(0, players ?? 0);
    const spectatorsSafe = Math.max(0, spectators ?? 0);
    const detail = this.playersDetail(gameType, {
      blue: Math.max(0, blueTeam ?? 0),
      red: Math.max(0, redTeam ?? 0),
      active: Math.max(0, playersSafe - spectatorsSafe),
      spectators: spectatorsSafe,
    });

    return html`
      <span class="icon-container padded">
        <span class="icon players"></span>
        <span class="tooltip">
          <span class="arrow"></span>
          ${detail.tooltip}
        </span>
      </span>
      ${playersSafe} ${detail.breakdown}
    `;
  }

  /** The player count's tooltip and team breakdown for each mode. A switch
   *  over every one, so a new mode won't compile until someone decides what
   *  it shows. BTR, like legacy, has no breakdown. */
  private playersDetail(
    gameType: GameMode,
    counts: { blue: number; red: number; active: number; spectators: number },
  ): { tooltip: string; breakdown: TemplateResult | typeof nothing } {
    switch (gameType) {
      case 'ctf':
        return {
          tooltip: 'Players online (blue / red / spectators)',
          breakdown: html`
            <span class="greyed">
              &nbsp;&nbsp;(<span class="blue-team">${counts.blue}</span>&nbsp;/&nbsp;<span class="red-team"
                >${counts.red}</span
              >&nbsp;/&nbsp;${counts.spectators})
            </span>
          `,
        };
      case 'ffa':
        return {
          tooltip: 'Players online (playing / spectators)',
          breakdown: html`
            <span class="greyed">
              &nbsp;&nbsp;(<span class="active-players">${counts.active}</span>&nbsp;/&nbsp;${counts.spectators})
            </span>
          `,
        };
      case 'btr':
        return { tooltip: 'Players online', breakdown: nothing };
    }
  }

  /** Render ping in milliseconds */
  private renderPing() {
    const { ping } = this.uiState;

    // Show "--" if ping not yet available
    const pingDisplay = ping != null ? ping : '--';

    return html`
      <span class="icon-container padded">
        <span class="icon ping"></span>
        <span class="tooltip">
          <span class="arrow"></span>
          Connection latency
        </span>
      </span>
      ${pingDisplay}<span class="millis">ms</span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'game-stats': GameStats;
  }
}
